import type { ApiClient } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import type { HostToPopup, PopupToHost } from '@cef-ai/wallet';
import type { ApplicationsStore } from '~/stores';

export interface LogoutHandlerDeps {
  identity: Identity;
  apiClient: ApiClient;
  applicationsStore?: ApplicationsStore;
  send: (msg: PopupToHost) => void;
}

/**
 * `wallet:logout` (Task 7). Tears down BOTH the local session (keys + the
 * bearer-active JWT — `IdentityImpl.logout()` already clears `cachedJwt`
 * alongside the vault, Task 2) AND the server-side refresh session, so a
 * subsequent reload cannot silently resume via `apiClient.session.refresh()`.
 *
 * This handler is the right seam for the server call: it already holds both
 * `apiClient` and `identity`, and it runs on the wallet origin — the only
 * place the HttpOnly refresh cookie is sent/cleared. The SDK's
 * `EmbedWallet.logout()` just dispatches `wallet:logout` here and clears its
 * own host-page-side mirror state; it has no origin access to the cookie.
 *
 * `apiClient.session.logout()` failures (e.g. network error, or a redundant
 * call when the family is already revoked) are swallowed — mirroring the
 * SDK's existing swallow-and-clear-locally behavior for the transport call.
 * Local state (vault + JWT) always clears regardless, so the wallet ends up
 * unauthenticated either way. In the failure case the refresh cookie may
 * persist client-side; that's acceptable degraded behavior; a stale cookie
 * either fails at next use (family already revoked) or is caught by reuse
 * detection. The codebase has no logger/telemetry seam (checked: no
 * `console.*` calls, no logging module, anywhere under `src/`), so this
 * operationally-relevant failure is surfaced via `console.warn` — the
 * minimal diagnostic that doesn't affect the swallow-and-continue contract
 * above.
 */
export function createLogoutHandler(deps: LogoutHandlerDeps) {
  return async (msg: Extract<HostToPopup, { type: 'wallet:logout' }>, _meta: { origin: string }): Promise<void> => {
    try {
      await deps.apiClient.session.logout();
    } catch (err) {
      // Local logout still proceeds below regardless of server outcome —
      // but a failed server-side revoke means the refresh cookie may
      // persist, which is worth surfacing even though it's not fatal.
      console.warn('wallet:logout: server-side session revoke failed; refresh cookie may persist', err);
    }
    deps.identity.logout();
    deps.applicationsStore?.reset();
    deps.send({ type: 'wallet:result', id: msg.id, result: undefined });
  };
}
